import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaUsers, 
  FaTrophy, 
  FaCalendarAlt, 
  FaHeart, 
  FaTimes, 
  FaChevronLeft, 
  FaChevronRight,
  FaImages,
  FaEye
} from 'react-icons/fa';
import Navbar from '../components/Layout/Navbar';
import TopBar from '../components/Layout/TopBar';

const Galerie = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEquipe, setSelectedEquipe] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Catégories avec style EFAFC
  const categories = [
    { 
      id: 'all', 
      name: 'Toutes les photos', 
      icon: FaImages, 
      count: 0,
      description: 'Toute la galerie'
    },
    { 
      id: 'matchs', 
      name: 'Matchs', 
      icon: FaTrophy, 
      count: 0,
      description: 'Photos de matchs officiels'
    },
    { 
      id: 'entrainements', 
      name: 'Entraînements', 
      icon: FaUsers, 
      count: 0,
      description: 'Sessions d\'entraînement'
    },
    { 
      id: 'evenements', 
      name: 'Événements', 
      icon: FaCalendarAlt, 
      count: 0,
      description: 'Événements spéciaux'
    },
    { 
      id: 'supporters', 
      name: 'Supporters', 
      icon: FaHeart, 
      count: 0,
      description: 'Nos fidèles supporters'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterPhotos();
  }, [photos, selectedCategory, selectedEquipe, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les photos
      const photosQuery = query(
        collection(db, 'galerie'),
        orderBy('dateAjout', 'desc')
      );
      const photosSnapshot = await getDocs(photosQuery);
      const photosData = photosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateAjout: doc.data().dateAjout?.toDate()
      }));

      // Charger les équipes
      const equipesQuery = query(collection(db, 'equipes'));
      const equipesSnapshot = await getDocs(equipesQuery);
      const equipesData = equipesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPhotos(photosData);
      setEquipes(equipesData);
      
      console.log('📸 Photos chargées:', photosData.length);
      console.log('⚽ Équipes chargées:', equipesData.length);
      
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPhotos = () => {
    let filtered = [...photos];

    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(photo => photo.categorie === selectedCategory);
    }

    // Filtre par équipe
    if (selectedEquipe !== 'all') {
      filtered = filtered.filter(photo => photo.equipeId === selectedEquipe);
    }

    // Filtre par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(photo =>
        photo.titre?.toLowerCase().includes(search) ||
        photo.description?.toLowerCase().includes(search) ||
        photo.evenement?.toLowerCase().includes(search) ||
        photo.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    }

    setFilteredPhotos(filtered);
  };

  const openLightbox = (photo, index) => {
    setSelectedPhoto(photo);
    setCurrentPhotoIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
    document.body.style.overflow = 'unset';
  };

  const navigatePhoto = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentPhotoIndex + 1) % filteredPhotos.length
      : (currentPhotoIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
    
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(filteredPhotos[newIndex]);
  };

  const getPhotoCount = (categoryId) => {
    if (categoryId === 'all') return photos.length;
    return photos.filter(p => p.categorie === categoryId).length;
  };

  const getEquipeCount = (equipeId) => {
    if (equipeId === 'all') return photos.length;
    return photos.filter(p => p.equipeId === equipeId).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
        <TopBar />
        <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
          <div className="relative w-[90vw] max-w-2xl">
            <Navbar />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block rounded-full h-16 w-16 border-4 border-[#FFE400] border-t-transparent mx-auto mb-4"
            />
            <p className="text-xl text-blue-200">Chargement de la galerie...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
      <TopBar />
      <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
        <div className="relative w-[90vw] max-w-2xl">
          <Navbar />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header avec glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10">
            <div className="text-center">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="p-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl shadow-lg">
                  <FaImages className="text-3xl text-[#001B56]" />
                </div>
                <h1 className="text-4xl font-bold text-white">
                  Galerie EFAFC
                </h1>
              </div>
              <p className="text-blue-200 text-lg max-w-2xl mx-auto">
                Revivez les plus beaux moments de notre club à travers notre collection de photos 📸
              </p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#FFE400]">{photos.length}</div>
                  <div className="text-sm text-blue-200">Photos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#FFE400]">{getPhotoCount('matchs')}</div>
                  <div className="text-sm text-blue-200">Matchs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#FFE400]">{getPhotoCount('entrainements')}</div>
                  <div className="text-sm text-blue-200">Entraînements</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#FFE400]">{getPhotoCount('evenements')}</div>
                  <div className="text-sm text-blue-200">Événements</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Barre de recherche et filtres */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 backdrop-blur-md bg-white/10 rounded-2xl p-6 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-300" />
              <input
                type="text"
                placeholder="Rechercher une photo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-[#FFE400] focus:border-transparent"
              />
            </div>

            {/* Bouton filtres mobile */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-2 px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white hover:bg-white/30 transition-colors"
            >
              <FaFilter />
              Filtres
            </button>
          </div>

          {/* Filtres desktop et mobile */}
          <div className={`mt-6 space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            {/* Catégories */}
            <div>
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <FaFilter className="text-[#FFE400]" />
                Catégories
              </h3>
              <div className="flex flex-wrap gap-3">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  const count = getPhotoCount(category.id);
                  return (
                    <motion.button
                      key={category.id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-3 px-6 py-3 rounded-2xl transition-all duration-300 backdrop-blur-md border ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] border-[#FFE400] shadow-xl'
                          : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                      }`}
                    >
                      <IconComponent className="text-lg" />
                      <div className="text-left">
                        <div className="font-semibold">{category.name}</div>
                        <div className="text-sm opacity-80">{count} photos</div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Équipes */}
            <div>
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <FaUsers className="text-[#FFE400]" />
                Équipes
              </h3>
              <div className="flex flex-wrap gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedEquipe('all')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 backdrop-blur-md border ${
                    selectedEquipe === 'all'
                      ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] border-[#FFE400]'
                      : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                  }`}
                >
                  <span className="font-medium">Toutes les équipes</span>
                  <span className="text-sm opacity-80">({getEquipeCount('all')})</span>
                </motion.button>
                {equipes.map((equipe) => (
                  <motion.button
                    key={equipe.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedEquipe(equipe.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 backdrop-blur-md border ${
                      selectedEquipe === equipe.id
                        ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] border-[#FFE400]'
                        : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                    }`}
                  >
                    <span className="font-medium">{equipe.nom}</span>
                    <span className="text-sm opacity-80">({getEquipeCount(equipe.id)})</span>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Résultats */}
        {filteredPhotos.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 text-center"
          >
            <p className="text-blue-200">
              <span className="text-[#FFE400] font-bold">{filteredPhotos.length}</span> photo{filteredPhotos.length > 1 ? 's' : ''} trouvée{filteredPhotos.length > 1 ? 's' : ''}
            </p>
          </motion.div>
        )}

        {/* Grille de photos */}
        <AnimatePresence>
          {filteredPhotos.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 backdrop-blur-md bg-white/10 rounded-3xl border border-white/20"
            >
              <FaImages className="mx-auto text-8xl text-blue-300 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">Aucune photo trouvée</h3>
              <p className="text-blue-200 text-lg">
                Essayez de modifier vos critères de recherche ou vos filtres
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredPhotos.map((photo, index) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -10 }}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(photo, index)}
                >
                  <div className="backdrop-blur-md bg-white/10 rounded-3xl border border-white/20 overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500">
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={photo.url}
                        alt={photo.titre}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute top-4 right-4">
                          <FaEye className="text-white text-xl" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="font-bold text-white text-sm mb-1 line-clamp-2">{photo.titre}</h3>
                          {photo.evenement && (
                            <p className="text-[#FFE400] text-xs">{photo.evenement}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedPhoto && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeLightbox}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative max-w-6xl max-h-[90vh] backdrop-blur-md bg-white/10 rounded-3xl border border-white/20 overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header lightbox */}
                <div className="absolute top-0 left-0 right-0 z-10 p-6 bg-gradient-to-b from-black/50 to-transparent">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <h3 className="text-white font-bold text-xl">{selectedPhoto.titre}</h3>
                      <span className="text-[#FFE400] text-sm">
                        {currentPhotoIndex + 1} / {filteredPhotos.length}
                      </span>
                    </div>
                    <button
                      onClick={closeLightbox}
                      className="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>

                {/* Navigation */}
                {filteredPhotos.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigatePhoto('prev');
                      }}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                    >
                      <FaChevronLeft size={24} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigatePhoto('next');
                      }}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                    >
                      <FaChevronRight size={24} />
                    </button>
                  </>
                )}

                {/* Image et infos */}
                <div className="flex flex-col lg:flex-row h-full">
                  <div className="flex-1 flex items-center justify-center p-6 pt-20 lg:pt-6">
                    <img
                      src={selectedPhoto.url}
                      alt={selectedPhoto.titre}
                      className="max-w-full max-h-full object-contain rounded-xl"
                    />
                  </div>

                  {/* Panel d'informations */}
                  <div className="lg:w-80 p-6 bg-gradient-to-b from-black/50 to-black/70 backdrop-blur-md border-l border-white/20">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-white font-bold text-xl mb-2">{selectedPhoto.titre}</h3>
                        {selectedPhoto.description && (
                          <p className="text-blue-200 text-sm leading-relaxed">{selectedPhoto.description}</p>
                        )}
                      </div>

                      {selectedPhoto.evenement && (
                        <div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56]">
                            {selectedPhoto.evenement}
                          </span>
                        </div>
                      )}

                      {selectedPhoto.tags && selectedPhoto.tags.length > 0 && (
                        <div>
                          <p className="text-blue-200 text-sm mb-2">Tags:</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedPhoto.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-white/20 rounded-lg text-white text-xs"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="border-t border-white/20 pt-4 space-y-2">
                        <p className="text-blue-200 text-sm">
                          📅 {selectedPhoto.dateAjout?.toLocaleDateString('fr-FR') || 'Date inconnue'}
                        </p>
                        {selectedPhoto.categorie && (
                          <p className="text-blue-200 text-sm">
                            🏷️ {categories.find(c => c.id === selectedPhoto.categorie)?.name || selectedPhoto.categorie}
                          </p>
                        )}
                        {selectedPhoto.equipeId && equipes.find(e => e.id === selectedPhoto.equipeId) && (
                          <p className="text-blue-200 text-sm">
                            ⚽ {equipes.find(e => e.id === selectedPhoto.equipeId)?.nom}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Galerie;